import React, { useState, useEffect } from "react";
import classNames from "classnames/bind";
import useSWR from "swr";
import { Form, Field } from "react-final-form";

import { CATEGORIES } from "../../api/categories";
import { Button } from "../../components/Button";

import { DietanamnezItemForm } from "types/DietanamnezItem";

import styles from "./DietanamnezTileForm.module.css";
import { FrequencyList } from "components/FrequencyList";

const cx = classNames.bind(styles);

type Props = {
  initialValues?: DietanamnezItemForm;
  onSubmit: (values?: DietanamnezItemForm) => Promise<void>;
  unitLabel: string;
  portions: number[];
  tutorialWasShown: boolean;
  showFrequencyWindow:boolean;
  setShowFrequencyWindow: React.Dispatch<React.SetStateAction<boolean>>
  state: string;
  setState: React.Dispatch<React.SetStateAction<"FILLED" | "DEFAULT" | "FIELD">>,
  inputPlaceholder: string;
  isFormSubmitting: boolean
};

export const DietanamnezTileForm: React.FC<Props> = ({
  initialValues,
  onSubmit,
  unitLabel,
  portions,
  tutorialWasShown,
  showFrequencyWindow,
  setShowFrequencyWindow,
  state,
  setState,
  inputPlaceholder,
  isFormSubmitting
}) => {

  const [placeholder, setPlaceholder] = useState<string>(inputPlaceholder)

  const changePlaceholder = (text: string) => setPlaceholder(text)
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { data: frequencies = [] } = useSWR<
    {
      key: string;
      custom_частота: string;
    }[]
  >([CATEGORIES.FREQUENCY, 86]);

  useEffect(() => {
    if (state === "FIELD" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [state])

  if (!frequencies.length || !tutorialWasShown) {
    return null;
  }

  return (
    <Form<Partial<DietanamnezItemForm>>
      initialValues={{
        custom_количество: initialValues?.custom_количество,
        custom_частота_употребления: initialValues?.custom_частота_употребления,
      }}
      validate={(values: Partial<DietanamnezItemForm>) => {
        let errors: { [key: string]: string } = {};

        if (!values.custom_количество || values.custom_количество <= 0) {
          errors.custom_количество = " ";
        }

        if (!values.custom_частота_употребления) {
          errors.custom_частота_употребления = " ";
        }

        if (Object.keys(errors).length) {
          return errors;
        }

        return undefined;
      }}
      validateOnMount
      onSubmit={(values) => onSubmit(values as DietanamnezItemForm)}
    >
      {({ handleSubmit, values, form, valid }) =>
      {
        const frequencyItem =  frequencies.find(
            (item) => item.key === values?.custom_частота_употребления
        )

        return (
          <form onSubmit={handleSubmit}>
            {state === "DEFAULT" &&
              <>
                <p className={cx("question")}>Вы употребляете в пищу это блюдо?</p>
                <div className={cx("buttons")}>
                  <Button
                    color={"dark"}
                    type="button"
                    size="medium"
                    onClick={onSubmit}
                    isLoading={isFormSubmitting}
                  >
                    Не употребляю
                  </Button>
                  <Button
                    color={"dark"}
                    type="button"
                    size="medium"
                    isLoading={isFormSubmitting}
                    onClick={() => {
                      setShowFrequencyWindow(true);
                    }}
                  >
                    Употребляю
                  </Button>
                </div>
              </>
            }
            {state !== "FILLED" && showFrequencyWindow && (
              <FrequencyList
                setShowFrequencyWindow={setShowFrequencyWindow}
                items={frequencies}
                onSubmit={onSubmit}
                isFormSubmitting={isFormSubmitting}
                onChange={(key) => {
                  form.change("custom_частота_употребления", key);
                  setState("FIELD");
                }}
              />
            )}
            {state === "FILLED" && (
              <>
                <p className={cx("title")}>Вы выбрали</p>
                <p className={cx("title", "title_frequency")}>
                  {frequencyItem ?
                    <>
                      {
                        frequencyItem?.custom_частота
                      }{" "}
                      по {values.custom_количество} {unitLabel}
                    </>
                    :
                    "Никогда / очень редко"
                  }
                </p>
                <div className={cx("actions")}>
                  <div className={cx("button")}>
                    <Button
                      block
                      type="button"
                      size="medium"
                      onClick={() => setState("DEFAULT")}
                    >
                      Изменить
                    </Button>
                  </div>
                </div>
              </>
            )}
            {state === "FIELD" && (
              <>
                <p className={cx("title", "title_frequency")}>
                  {
                    frequencies.find(
                      (item) => item.key === values.custom_частота_употребления
                    )!.custom_частота
                  }
                </p>
                <div className={cx("inputContainer")} onBlur={() => changePlaceholder(inputPlaceholder)} onFocus={() => changePlaceholder('Выберите или введите')}>
                  <Field name="custom_количество" type="number">
                    {({ input, meta }) => (
                      <>
                        <div className={cx("inputContent")}>
                          <input
                            ref={inputRef}
                            type="text"
                            pattern="[0-9]*"
                            inputMode="numeric"
                            className={cx("input")}
                            placeholder={placeholder}
                            {...input}
                          />
                          <div className={cx("button")}>
                            <Button
                              block
                              type="submit"
                              size="medium"
                              disabled={!valid}
                              isLoading={isFormSubmitting}
                              onClick={() => {
                                window.scrollTo(0, 0)
                              }}>
                              Далее
                            </Button>
                          </div>
                        </div>
                        <ul className={cx("portions")}>
                          {portions.map((portion) => (
                            <li
                              key={portion}
                              className={cx("portion")}
                              onClick={() => input.onChange(portion)}
                            >
                              {portion} {unitLabel}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Field>
                </div>
              </>
            )}
          </form>
        )}
      }
    </Form>
  );
};
